import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { cn } from '@/lib/utils';
import { useForm } from 'react-hook-form';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  ArrowRightLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  EyeOff,
  Film,
  PanelRight,
  Download,
  ExternalLink,
  Loader2,
  Pencil,
  Radar,
  RotateCcw,
  ScanSearch,
  Tags,
  Users,
} from 'lucide-react';
import { useColumnPreferences, type ColumnDef } from '@/hooks/useColumnPreferences';
import { ColumnPicker } from '@/components/ui/column-picker';
import { toast } from 'sonner';
import {
  useAdjustInvite,
  useAdminInvites,
  useAdminContacted,
  useAdminInviter,
  useAdminInviters,
  useAdminNiches,
  useAdminTeams,
  useAdminTeamSettings,
  useDeleteInvite,
  useDiscoveryFolders,
  useExportInvites,
  useInvitePosts,
  useInvitePostsBatch,
  useInviteStageCounts,
  useReassignInvite,
  useRejectInvite,
  useResolveInvite,
  useUpdateInvite,
} from '@/hooks/useAdmin';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlState } from '@/hooks/useUrlState';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { DEFAULT_EXCLUDED_LINKS, teamSection } from '@/lib/teams';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { usePendingKeys } from '@/hooks/usePendingKeys';
import { formatMoney } from '@/lib/money';
import { formatDate, formatDateTime } from '@/lib/dates';
import {
  GLOSSARY,
  INVITE_STAGES,
  creatorTypeLabel,
  inviteStage,
  inviteStatusLabel,
  teamLabel,
  verdictLabel,
  verdictTone,
} from '@/lib/labels';
import {
  CLEAR_SAVED_VIEW,
  INVITE_SINCE_OPTIONS,
  INVITE_SORTS,
  SIZE_FILTERS,
  actorLabel,
  activeSavedView,
  clampPage,
  cleanBound,
  contactedQueryParams,
  inviteTeamScope,
  invitesQueryParams,
  resolveSort,
  resolveStageSelection,
  savedViewPatch,
  sinceLabel,
  sinceStart,
  sortLabel,
  stageAgeLabel,
  stageCountsQueryParams,
  stageTabFor,
  type InviteSortKey,
  type InviteStageFilter,
  type SavedInviteView,
} from '@/lib/moneyPages';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ContactedTable } from '@/components/invites/ContactedTable';
import { InviteProofInline, InviteScreenshotAuditDialog } from '@/components/invites/InviteScreenshotAudit';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { InviteStageTabs, type InviteStageTab } from '@/components/invites/InviteStageTabs';
import { InviteSavedViews } from '@/components/invites/InviteSavedViews';
import { OutreacherPicker } from '@/components/money/OutreacherPicker';
import { Inbox } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/ui/pill';
import { Tag, channelLabel, channelToneFor } from '@/components/ui/tag';
import { PlatformIcon } from '@/components/PlatformIcon';
import { PaymentHistoryTab } from './PaymentHistoryTab';
import { Button } from '@/components/ui/button';
import { RowActions, type RowActionItem } from '@/components/ui/row-actions';
import { Pagination } from '@/components/ui/pagination';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { NichesPicker } from '@/components/NichesPicker';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { RecheckAllButton } from '@/components/invites/RecheckAllButton';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { ActiveFilters } from '@/components/ui/active-filters';
import {
  INVITE_CHANNELS,
  type InviteChannel,
  type InvitePost,
  type OutreachInvite,
} from '@/api/types';

const CHANNELS = INVITE_CHANNELS;

const PAGE_SIZE = 50;

/**
 * Fields the invites endpoint returns that the shared type doesn't carry
 * yet: who reviewed the proof, and when the creator joined (null on invites
 * that joined before it was recorded).
 */
type InviteRow = OutreachInvite & {
  screenshotReviewedBy?: string | null;
  verifiedAt?: string | null;
};

/** What the outreacher's team tracks, for the stage pill. */
type TeamTracks = (inv: OutreachInvite) => boolean | undefined;

/** Anchor for the sheet's "View posts" action. */
const CLIPS_SECTION_ID = 'invite-sheet-clips';

/**
 * Defaults are the columns that fit a laptop screen without horizontal
 * scroll: Creator · Outreacher · Stage · Logged · Owed. Channel, creator
 * type, niche and source ride along under each handle; followers and clip
 * views are in the detail sheet's facts. Everything else is one click away
 * in the Columns picker. The prefs key is versioned (v3) so layouts saved
 * against the old, wider default don't pin it.
 */
const ADMIN_COLUMNS: ColumnDef[] = [
  { key: 'target', label: 'Creator', locked: true },
  { key: 'outreachedBy', label: 'Outreacher', defaultVisible: true },
  { key: 'stage', label: 'Stage', defaultVisible: true },
  { key: 'date', label: 'Logged', defaultVisible: true },
  { key: 'owed', label: 'Owed', defaultVisible: true },
  { key: 'followers', label: 'Followers', defaultVisible: false },
  { key: 'avgViews', label: 'Avg views', defaultVisible: false },
  { key: 'views', label: 'Clip views', defaultVisible: false },
  { key: 'channel', label: 'Channel', defaultVisible: false },
  { key: 'category', label: 'Creator type', defaultVisible: false },
  { key: 'niche', label: 'Niche', defaultVisible: false },
  { key: 'source', label: 'Source', defaultVisible: false },
  { key: 'proof', label: 'Proof', defaultVisible: false },
  { key: 'posts', label: 'Posts', defaultVisible: false },
  { key: 'clipLinks', label: 'Top clip', defaultVisible: false },
  { key: 'campaign', label: 'Campaign', defaultVisible: false },
];

export function AdminInvites(): JSX.Element {
  // Team comes from the header switcher (or is pinned on a team section's
  // pages); every other filter lives in the URL so a filtered view can be linked.
  const { team, setTeam, locked } = useTeamScope();
  const mainScopeLabel = useMainScopeLabel();
  const lockedSection = locked ? teamSection(team) : undefined;
  const location = useLocation();
  const [f, set] = useUrlState({
    search: '',
    // `[]` is the All tab. `status` is only read: older links (and other
    // pages) still say `?status=PENDING,VERIFIED`, which maps onto stages.
    stage: [] as string[],
    status: [] as string[],
    channel: '',
    inviterId: '',
    niche: '',
    dateFrom: '',
    dateTo: '',
    // Quick rolling window (24h, 3d, …) on when the reply was logged. Wins
    // over From / To, which picking one clears.
    since: '',
    // When the creator posted / joined, from Home's "this period" counts.
    // No inputs of their own: they arrive by link and leave by their chips.
    convertedFrom: '',
    convertedTo: '',
    verifiedFrom: '',
    verifiedTo: '',
    owed: false as boolean,
    source: '',
    // Creator-size windows (best-known followers, discovery's average views).
    minFollowers: '',
    maxFollowers: '',
    minAvgViews: '',
    maxAvgViews: '',
    // List order. Empty = newest logged first.
    sort: '',
    dir: '',
    view: 'invites',
    page: 1,
    open: '',
  });
  const stages = useMemo(() => resolveStageSelection(f.stage, f.status), [f.stage, f.status]);
  const stageTab = stageTabFor(stages);
  // Two lists, not a filter: Messaged rows have no invite behind them.
  const contactedOnly = f.view === 'contacted';
  const page = Math.max(1, f.page);
  // Debounce so each keystroke doesn't fire a query against the full invites
  // table. 250ms matches the prospect lookup page.
  const debouncedSearch = useDebounce(f.search, 250);
  // The range boxes are typed a digit at a time; wait for a pause like the search box does.
  const debouncedSize = useDebounce(SIZE_FILTERS.map((k) => cleanBound(f[k])).join('|'), 350);
  const invalidateMoney = useInvalidateMoney();
  const { user } = useUser();
  const me = useMemo(() => ({ id: user?.id ?? null, name: user?.fullName ?? null }), [user?.id, user?.fullName]);

  const [adjusting, setAdjusting] = useState<OutreachInvite | null>(null);
  const [rejecting, setRejecting] = useState<OutreachInvite | null>(null);
  const [unrejecting, setUnrejecting] = useState<OutreachInvite | null>(null);
  const [reassigning, setReassigning] = useState<OutreachInvite | null>(null);
  const [reassignName, setReassignName] = useState<string>('');
  const [editing, setEditing] = useState<OutreachInvite | null>(null);
  const [hiding, setHiding] = useState<OutreachInvite | null>(null);
  const [auditing, setAuditing] = useState<OutreachInvite | null>(null);
  // The invite open in the detail sheet. The sheet prefers the refetched row
  // with the same id (so it reflects an action it just took) and falls back
  // to this copy when that row leaves the list — rejecting an invite while
  // Not paid is filtered out shouldn't slam the sheet shut.
  const [selected, setSelected] = useState<OutreachInvite | null>(null);
  // The `?open=` value already acted on, so a lagging URL update can't
  // re-open a sheet that was just closed or moved past.
  const handledOpen = useRef('');
  const adjustForm = useForm<{ amount: string; reason: string }>({
    defaultValues: { amount: '0', reason: '' },
  });
  const rejectForm = useForm<{ reason: string }>({ defaultValues: { reason: '' } });
  const reassignForm = useForm<{ inviterId: string; reason: string }>({
    defaultValues: { inviterId: '', reason: '' },
  });
  /**
   * Edit handle & niches. `niches` is the array of selected niche codes —
   * the UI is a chip picker (NichesPicker) matching the create-time pattern
   * on LogInvite, so admins don't have to remember comma syntax or exact
   * code casing.
   */
  const editForm = useForm<{ targetIdentifier: string; niches: string[] }>({
    defaultValues: { targetIdentifier: '', niches: [] },
  });
  const cols = useColumnPreferences('admin-invites-v3', ADMIN_COLUMNS);
  const adjustMutation = useAdjustInvite();
  const rejectMutation = useRejectInvite();
  const reassignMutation = useReassignInvite();
  const resolveMutation = useResolveInvite();
  const updateMutation = useUpdateInvite();
  const deleteMutation = useDeleteInvite();
  const exportMutation = useExportInvites();
  // Row actions in flight, per invite: one mutation hook only tracks its latest call.
  const rowPending = usePendingKeys();

  function openSheet(inv: OutreachInvite): void {
    handledOpen.current = inv.publicId;
    setSelected(inv);
    set({ open: inv.publicId });
  }
  function closeSheet(): void {
    setSelected(null);
    set({ open: '' });
  }

  function openAdjust(inv: OutreachInvite): void {
    adjustForm.reset({ amount: '0', reason: '' });
    setAdjusting(inv);
  }
  function openReject(inv: OutreachInvite): void {
    rejectForm.reset({ reason: '' });
    setRejecting(inv);
  }
  function openReassign(inv: OutreachInvite): void {
    reassignForm.reset({ inviterId: String(inv.inviterId), reason: '' });
    setReassignName('');
    setReassigning(inv);
  }
  function openEdit(inv: OutreachInvite): void {
    editForm.reset({
      targetIdentifier: inv.targetIdentifier,
      niches: [...inv.niches],
    });
    setEditing(inv);
  }
  function openHide(inv: OutreachInvite): void {
    setHiding(inv);
  }
  async function onHideConfirm(): Promise<void> {
    if (!hiding) return;
    try {
      await deleteMutation.mutateAsync(hiding.publicId);
      invalidateMoney();
      toast.success(`Hid the invite for ${hiding.targetIdentifier}`);
      setHiding(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't hide the invite");
    }
  }
  async function onEditSubmit(values: {
    targetIdentifier: string;
    niches: string[];
  }): Promise<void> {
    if (!editing) return;
    // Strip leading @ + trim — backend does the same on its side but doing
    // it here lets us short-circuit no-op submits and produce nicer toasts.
    const handle = values.targetIdentifier.trim().replace(/^@+/, '');
    if (!handle) {
      toast.error('Enter a handle');
      return;
    }
    const niches = values.niches;
    const handleChanged = handle !== editing.targetIdentifier;
    const nichesChanged =
      niches.length !== editing.niches.length ||
      niches.some((n, i) => n !== editing.niches[i]);
    if (!handleChanged && !nichesChanged) {
      toast.info('No changes to save');
      setEditing(null);
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: editing.id,
        body: {
          ...(handleChanged ? { targetIdentifier: handle } : {}),
          ...(nichesChanged ? { niches } : {}),
        },
      });
      invalidateMoney();
      toast.success(`Saved "${handle}"`);
      setEditing(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }
  async function onUnreject(inv: OutreachInvite): Promise<void> {
    try {
      const done = await rowPending.run(`unreject:${inv.id}`, () =>
        updateMutation.mutateAsync({
          id: inv.id,
          body: { status: 'VERIFIED' },
        }),
      );
      if (!done) return;
      invalidateMoney();
      toast.success(`"${inv.targetIdentifier}" is back to Joined. The write-off stays until you adjust it.`);
      setUnrejecting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onReassignSubmit(values: { inviterId: string; reason: string }): Promise<void> {
    if (!reassigning) return;
    const inviterId = Number.parseInt(values.inviterId, 10);
    if (!Number.isFinite(inviterId) || inviterId <= 0) {
      toast.error('Pick an outreacher');
      return;
    }
    if (inviterId === reassigning.inviterId) {
      toast.error('Already with that outreacher');
      return;
    }
    try {
      await reassignMutation.mutateAsync({
        inviteId: reassigning.id,
        inviterId,
        reason: values.reason.trim() || undefined,
      });
      invalidateMoney();
      toast.success(`Reassigned to ${reassignName || inviterNameById.get(inviterId) || 'the new outreacher'}`);
      setReassigning(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onAdjustSubmit(values: { amount: string; reason: string }): Promise<void> {
    if (!adjusting) return;
    try {
      await adjustMutation.mutateAsync({
        inviteId: adjusting.id,
        amount: values.amount,
        reason: values.reason,
      });
      invalidateMoney();
      toast.success(`Owed amount adjusted by $${values.amount}`);
      setAdjusting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onRejectSubmit(values: { reason: string }): Promise<void> {
    if (!rejecting) return;
    try {
      const result = await rejectMutation.mutateAsync({
        inviteId: rejecting.id,
        reason: values.reason,
      });
      invalidateMoney();
      toast.success(`Rejected. Wrote off $${formatMoney(result.netZeroedBy)}`);
      setRejecting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  /** Same check as the morning job: refreshes status, clip count and followers. */
  function onRecheck(inv: OutreachInvite): void {
    void rowPending
      .run(`recheck:${inv.id}`, () => resolveMutation.mutateAsync(inv.id))
      .then((data) => {
        if (!data) return;
        invalidateMoney();
        const r = data.result;
        if (!r) {
          toast.warning("Clipping didn't return anything for this creator");
          return;
        }
        // Prefer the attribution-aware count over the raw all-time
        // approvedClipCount so the toast tracks the status transition. A
        // divergence (e.g. `0 of 1 in window`) is exactly the "why didn't this
        // convert?" signal we want an admin to see at a glance.
        const attr = data.attribution;
        const clipFragment = attr
          ? `${attr.windowedPostCount} of ${attr.allTimeApprovedClipCount ?? r.approvedClipCount} clips count`
          : `${r.approvedClipCount} clips`;
        const followers =
          r.followerCount !== null ? ` · ${formatFollowerCount(r.followerCount)} followers` : '';
        toast.success(`Checked: ${inviteStatusLabel(data.invite.status)} · ${clipFragment}${followers}`);
      })
      .catch((err: Error) => toast.error(err.message));
  }

  // ── Actions ─────────────────────────────────────────────────────────────
  // One primary action per invite, picked by stage, and everything else in a
  // menu: the row's kebab, and the sheet's More. Destructive items (Reject,
  // Hide) sit at the bottom of either menu behind a separator.

  function recheckItem(inv: OutreachInvite): RowActionItem {
    const pending = rowPending.has(`recheck:${inv.id}`);
    return {
      label: 'Re-check status',
      icon: pending ? Loader2 : Radar,
      disabled: pending,
      onClick: () => onRecheck(inv),
    };
  }
  function unrejectItem(inv: OutreachInvite): RowActionItem {
    return { label: 'Un-reject…', icon: RotateCcw, onClick: () => setUnrejecting(inv) };
  }
  function proofItem(inv: OutreachInvite): RowActionItem {
    return {
      label: inv.screenshotKey ? 'Review proof' : 'No proof uploaded',
      icon: ScanSearch,
      disabled: !inv.screenshotKey,
      onClick: () => setAuditing(inv),
    };
  }

  /** Edit, adjust, reassign, reject, hide — whatever the invite's state allows. */
  function moreActions(inv: OutreachInvite): RowActionItem[] {
    const isHidden = inv.deletedAt !== null;
    const isTerminal = inv.status === 'REJECTED';
    const items: (RowActionItem | false)[] = [
      !isHidden && { label: 'Edit handle & niches', icon: Tags, onClick: () => openEdit(inv) },
      !isHidden && !isTerminal && { label: 'Adjust owed amount', icon: Pencil, onClick: () => openAdjust(inv) },
      !isHidden && !isTerminal && {
        label: 'Reassign to another outreacher',
        icon: ArrowRightLeft,
        onClick: () => openReassign(inv),
      },
      !isHidden && !isTerminal && {
        label: 'Reject invite',
        icon: Ban,
        onClick: () => openReject(inv),
        destructive: true,
      },
      // SPA-618. Team-gated soft delete. The admin route bypasses the gate,
      // but the UI mirrors it so the action only appears where the team
      // opted in.
      !isHidden &&
        (canHideByTeam.get(inviterTeamById.get(inv.inviterId) ?? '') ?? false) && {
          label: 'Hide invite',
          icon: EyeOff,
          onClick: () => openHide(inv),
          destructive: true,
        },
    ];
    return items.filter((i): i is RowActionItem => !!i);
  }

  /** The row's kebab: re-check (or un-reject), proof, then the rest. */
  function rowMenu(inv: OutreachInvite): RowActionItem[] {
    return [
      inv.status === 'REJECTED' ? unrejectItem(inv) : recheckItem(inv),
      proofItem(inv),
      ...moreActions(inv),
    ];
  }

  type SheetPrimary = 'recheck' | 'posts' | 'unreject' | null;
  /**
   * The sheet's one primary action, by stage: Un-reject for Not paid, View
   * posts for Posted (when clipping has the creator's posts), Re-check
   * status otherwise. Hidden invites get none.
   */
  function sheetPrimaryKind(inv: OutreachInvite): SheetPrimary {
    if (inv.deletedAt !== null) return null;
    if (inv.status === 'REJECTED') return 'unreject';
    if (inv.status === 'CONVERTED' && inv.matchedUserId) return 'posts';
    return 'recheck';
  }
  function sheetPrimary(inv: OutreachInvite): JSX.Element | null {
    const kind = sheetPrimaryKind(inv);
    if (kind === 'unreject') {
      return (
        <Button
          size="sm"
          onClick={() => setUnrejecting(inv)}
          title="Moves the invite back to Joined. The write-off stays; adjust the owed amount to restore it."
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Un-reject
        </Button>
      );
    }
    if (kind === 'posts') {
      return (
        <Button
          size="sm"
          onClick={() => document.getElementById(CLIPS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          title="Jump to this creator's approved clips"
        >
          <Film className="mr-1.5 h-3.5 w-3.5" /> View posts
        </Button>
      );
    }
    if (kind === 'recheck') {
      const pending = rowPending.has(`recheck:${inv.id}`);
      return (
        <Button
          size="sm"
          aria-label={`Re-check status for ${inv.targetIdentifier}`}
          title="Same check as the morning job: refreshes status, clip count and followers."
          disabled={pending}
          onClick={() => onRecheck(inv)}
        >
          {pending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Radar className="mr-1.5 h-3.5 w-3.5" />}
          Re-check status
        </Button>
      );
    }
    return null;
  }
  /** The sheet's More menu: everything the primary button isn't. */
  function sheetMore(inv: OutreachInvite): RowActionItem[] {
    const kind = sheetPrimaryKind(inv);
    return [
      kind !== 'recheck' && inv.status !== 'REJECTED' ? recheckItem(inv) : null,
      proofItem(inv),
      ...moreActions(inv),
    ].filter((i): i is RowActionItem => i !== null);
  }

  const sizeActive = SIZE_FILTERS.some((k) => !!cleanBound(f[k]));
  const secondaryActive = !!(f.channel || f.inviterId || f.niche || f.source || f.dateFrom || f.dateTo || f.owed || sizeActive);
  const order = resolveSort(f.sort, f.dir);
  /** Order the list by a column; a hidden column is shown so the values being sorted are visible. */
  function sortBy(key: InviteSortKey, dir: 'asc' | 'desc'): void {
    const column = INVITE_SORTS.find((x) => x.key === key)?.column;
    if (column && !cols.isVisible(column)) cols.toggle(column);
    const isDefault = key === 'logged' && dir === 'desc';
    set({ sort: isDefault ? '' : key, dir: isDefault ? '' : dir, page: 1 });
  }
  /** Header click: high-to-low first, then low-to-high, on the same column. */
  function toggleSort(key: InviteSortKey): void {
    sortBy(key, order.key === key && order.dir === 'desc' ? 'asc' : 'desc');
  }
  const [showMore, setShowMore] = useState(secondaryActive);

  const inviters = useAdminInviters({ limit: 200 });
  const niches = useAdminNiches();
  const teams = useAdminTeams();
  const teamSettings = useAdminTeamSettings();
  const discoveryFolders = useDiscoveryFolders();
  const filteredInviter = useAdminInviter(f.inviterId ? Number(f.inviterId) : null);

  // inviterId → name lookup, built once per inviters load so each row is an
  // O(1) map.get instead of an O(n) find against the full inviters list.
  const inviterNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const inv of inviters.data ?? []) map.set(inv.id, inv.name);
    return map;
  }, [inviters.data]);
  // inviterId → team code (for joining team settings to each invite row).
  const inviterTeamById = useMemo(() => {
    const map = new Map<number, string>();
    for (const inv of inviters.data ?? []) map.set(inv.id, inv.team);
    return map;
  }, [inviters.data]);
  // team code → clippingResolveRequired. Unknown teams stay undefined so the
  // pill reads "Replied" rather than claiming manual tracking.
  const clippingByTeam = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of teamSettings.data ?? []) map.set(s.team, s.clippingResolveRequired);
    return map;
  }, [teamSettings.data]);
  const tracksForTeam = (code: string | null | undefined): boolean | undefined =>
    code ? clippingByTeam.get(code) : undefined;
  const teamTracks: TeamTracks = (inv) => tracksForTeam(inviterTeamById.get(inv.inviterId));
  // team code → canDeleteInvites (SPA-618), gating "Hide invite".
  const canHideByTeam = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of teamSettings.data ?? []) map.set(s.team, s.canDeleteInvites);
    return map;
  }, [teamSettings.data]);
  const teamNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teams.data ?? []) map.set(t.code, t.displayName);
    return map;
  }, [teams.data]);
  const nicheNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of niches.data ?? []) map.set(n.code, n.displayName);
    return map;
  }, [niches.data]);
  const nicheName = (code: string): string => nicheNameByCode.get(code) ?? code;

  // ── Team scope ──────────────────────────────────────────────────────────
  // The Outreach route leaves Influencer and Vyral out (they have their own
  // sections) unless the admin picks one, or picks an outreacher. A locked
  // section route sends its own team.
  const scope = inviteTeamScope({ team, locked, inviterId: f.inviterId });
  const contactedScope = inviteTeamScope({ team, locked });
  const excludingSections = !!(contactedOnly ? contactedScope : scope).excludeTeam;

  // ── Stage tabs & saved views ────────────────────────────────────────────
  // `now` moves with each refetch, so ages and the saved views' "7 days ago"
  // don't go stale on a page left open.
  // A quick window's start is fixed when it's picked, so the query key (and
  // the list) stays put until the admin picks again.
  const sinceAnchor = useMemo(() => Date.now(), [f.since]); // eslint-disable-line react-hooks/exhaustive-deps
  const sinceFrom = sinceStart(f.since, sinceAnchor);
  const [minFollowers = '', maxFollowers = '', minAvgViews = '', maxAvgViews = ''] = debouncedSize.split('|');
  const listFilters = {
    ...f,
    search: debouncedSearch,
    minFollowers,
    maxFollowers,
    minAvgViews,
    maxAvgViews,
    stages,
    ...(sinceFrom ? { dateFrom: sinceFrom, dateTo: '' } : {}),
  };
  const filterParams = invitesQueryParams(listFilters, scope);
  const stageCounts = useInviteStageCounts(stageCountsQueryParams(listFilters, scope), { enabled: !contactedOnly });
  // Saved views set their own date range, so their counts ignore the page's.
  // Without a date range this is the same query as above (one request).
  const viewCounts = useInviteStageCounts(stageCountsQueryParams(listFilters, scope, { dates: false }), {
    enabled: !contactedOnly,
  });

  const invites = useAdminInvites({ ...filterParams, page, limit: PAGE_SIZE });
  const now = useMemo(() => new Date(), [invites.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const savedView = activeSavedView({ stages, dateFrom: f.dateFrom, dateTo: f.dateTo }, now);

  function selectStageTab(tab: InviteStageTab): void {
    set({ stage: tab === 'all' ? [] : [tab], status: [], page: 1 });
  }
  function selectSavedView(view: SavedInviteView): void {
    const patch = savedView === view ? CLEAR_SAVED_VIEW : savedViewPatch(view, now);
    set({ ...patch, since: '', page: 1 });
  }

  type ActiveFilter = { key: string; label: string; clear: () => void };
  const activeFilters: ActiveFilter[] = [
    f.search.trim()
      ? { key: 'search', label: `Search: "${f.search.trim()}"`, clear: () => set({ search: '', page: 1 }) }
      : null,
    // A tab already says which single stage is showing; a mix (from a link)
    // gets a chip, since no tab lights up for it.
    !contactedOnly && stageTab === null
      ? {
          key: 'stage',
          label: `Stage: ${describeStages(stages)}`,
          clear: () => set({ stage: [], status: [], page: 1 }),
        }
      : null,
    team && !locked
      ? {
          key: 'team',
          label: `Team: ${teamLabel(team, teamNameByCode.get(team))}`,
          clear: () => setTeam(''),
        }
      : null,
    f.channel
      ? { key: 'channel', label: `Channel: ${channelLabel(f.channel)}`, clear: () => set({ channel: '', page: 1 }) }
      : null,
    !contactedOnly && f.inviterId
      ? {
          key: 'inviter',
          label: `Outreacher: ${filteredInviter.data?.name ?? inviterNameById.get(Number(f.inviterId)) ?? '…'}`,
          clear: () => set({ inviterId: '', page: 1 }),
        }
      : null,
    !contactedOnly && f.niche
      ? { key: 'niche', label: `Niche: ${nicheName(f.niche)}`, clear: () => set({ niche: '', page: 1 }) }
      : null,
    !contactedOnly && sinceFrom
      ? { key: 'since', label: `Logged in the ${sinceLabel(f.since)}`, clear: () => set({ since: '', page: 1 }) }
      : null,
    !contactedOnly && !sinceFrom && f.dateFrom
      ? { key: 'from', label: `From: ${formatDate(`${f.dateFrom}T00:00:00`)}`, clear: () => set({ dateFrom: '', page: 1 }) }
      : null,
    !contactedOnly && !sinceFrom && f.dateTo
      ? { key: 'to', label: `To: ${formatDate(`${f.dateTo}T00:00:00`)}`, clear: () => set({ dateTo: '', page: 1 }) }
      : null,
    !contactedOnly && (f.convertedFrom || f.convertedTo)
      ? {
          key: 'posted-dates',
          label: `Posted: ${eventRangeLabel(f.convertedFrom, f.convertedTo)}`,
          clear: () => set({ convertedFrom: '', convertedTo: '', page: 1 }),
        }
      : null,
    !contactedOnly && (f.verifiedFrom || f.verifiedTo)
      ? {
          key: 'joined-dates',
          label: `Joined: ${eventRangeLabel(f.verifiedFrom, f.verifiedTo)}`,
          clear: () => set({ verifiedFrom: '', verifiedTo: '', page: 1 }),
        }
      : null,
    !contactedOnly && f.owed
      ? { key: 'owed', label: 'Owed only', clear: () => set({ owed: false, page: 1 }) }
      : null,
    ...SIZE_FILTERS.map((key) => {
      const v = cleanBound(f[key]);
      if (contactedOnly || !v) return null;
      const what = key.endsWith('Followers') ? 'followers' : 'avg views';
      return {
        key,
        label: `${key.startsWith('min') ? 'At least' : 'At most'} ${Number(v).toLocaleString()} ${what}`,
        clear: () => set({ [key]: '', page: 1 }),
      };
    }),
    !contactedOnly && !order.isDefault
      ? { key: 'sort', label: `Sorted: ${sortLabel(order.key, order.dir)}`, clear: () => set({ sort: '', dir: '', page: 1 }) }
      : null,
    !contactedOnly && f.source
      ? {
          key: 'discovery',
          label: `Source: ${f.source === '__any__' ? 'Any discovery queue' : f.source}`,
          clear: () => set({ source: '', page: 1 }),
        }
      : null,
  ].filter((x): x is ActiveFilter => x !== null);

  function clearAllFilters(): void {
    set({
      search: '',
      stage: [],
      status: [],
      channel: '',
      inviterId: '',
      niche: '',
      dateFrom: '',
      dateTo: '',
      since: '',
      convertedFrom: '',
      convertedTo: '',
      verifiedFrom: '',
      verifiedTo: '',
      owed: false,
      source: '',
      minFollowers: '',
      maxFollowers: '',
      minAvgViews: '',
      maxAvgViews: '',
      sort: '',
      dir: '',
      page: 1,
    });
    if (!locked && team) setTeam('');
  }

  async function onExportClick(): Promise<void> {
    try {
      await exportMutation.mutateAsync(filterParams);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't export invites");
    }
  }

  // Messaged but unanswered creators. These rows have no invite, so only
  // channel, team scope and search apply.
  const contacted = useAdminContacted(
    { ...contactedQueryParams({ search: debouncedSearch, channel: f.channel }, contactedScope), page, limit: PAGE_SIZE },
    contactedOnly,
  );

  const rows = useMemo(() => (invites.data?.data ?? []) as InviteRow[], [invites.data]);
  const meta = invites.data?.meta ?? { page, limit: PAGE_SIZE, total: rows.length };
  const contactedRows = contacted.data?.data ?? [];
  const contactedMeta =
    contacted.data?.meta ?? { page, limit: PAGE_SIZE, total: contactedRows.length };

  // Never sit on a page past the end: a narrower filter or a row leaving
  // after an action can shrink the total under the current page.
  const activeMeta = contactedOnly ? contacted.data?.meta : invites.data?.meta;
  useEffect(() => {
    if (!activeMeta) return;
    const clamped = clampPage(page, activeMeta.total, activeMeta.limit);
    if (clamped !== page) set({ page: clamped });
  }, [activeMeta, page, set]);

  // `?open=<invitePublicId>` opens that invite's sheet once its row is loaded.
  useEffect(() => {
    if (!f.open) {
      handledOpen.current = '';
      return;
    }
    if (handledOpen.current === f.open) return;
    const match = rows.find((r) => r.publicId === f.open);
    if (match) {
      handledOpen.current = f.open;
      setSelected(match);
    }
  }, [f.open, rows]);

  // A link asked for an invite that isn't in this page of results.
  const openMissing =
    !!f.open &&
    !contactedOnly &&
    !!invites.data &&
    !invites.isFetching &&
    selected?.publicId !== f.open &&
    !rows.some((r) => r.publicId === f.open);
  // The same link, for each team this view leaves out — where its invites live.
  const sectionHrefs = useMemo(() => {
    return DEFAULT_EXCLUDED_LINKS.map((s) => {
      const params = new URLSearchParams(location.search);
      params.delete('team');
      params.delete('page');
      if (s.pick) params.set('team', s.team);
      const qs = params.toString();
      return { ...s, href: `${s.base}${qs ? `?${qs}` : ''}` };
    });
  }, [location.search]);

  const matchedUserIds = useMemo(
    () => rows.map((r) => r.matchedUserId).filter((id): id is string => !!id),
    [rows],
  );
  // Only the Top clip, Campaign and Clip views columns read clipping posts,
  // and they're off by default. Also suppressed while the invites query is
  // still fetching — every page-flip / filter change recomputes `rows` (and
  // thus the ID set) before the parent's response arrives, so without this
  // gate the batch fires twice per navigation and in-flight POSTs stack up.
  const needsPosts = cols.isVisible('clipLinks') || cols.isVisible('campaign') || cols.isVisible('views');
  const postsBatch = useInvitePostsBatch(matchedUserIds, {
    enabled: needsPosts && !invites.isFetching,
  });
  const postsByUser = postsBatch.data?.byMatchedUserId ?? {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invites"
        description={
          lockedSection
            ? `${lockedSection.label} creators who replied and were logged, across every ${lockedSection.label} outreacher.`
            : 'Every creator who replied and was logged, across all outreachers.'
        }
        actions={<RecheckAllButton />}
      />

      <div className="space-y-3">
        {/* Two lists, not a filter: Messaged rows have no invite behind
            them, so they can't share the invite columns. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div role="tablist" aria-label="View" className="inline-flex h-9 items-center rounded-md border border-input p-0.5">
            {([
              {
                value: false,
                label: 'Invites',
                count: invites.isLoading ? null : meta.total,
              },
              {
                value: true,
                label: 'Messaged · no reply',
                count: contactedOnly && !contacted.isLoading ? contactedMeta.total : null,
              },
            ] as const).map((tab) => (
              <button
                key={tab.label}
                type="button"
                role="tab"
                aria-selected={contactedOnly === tab.value}
                onClick={() => {
                  setSelected(null);
                  set({ view: tab.value ? 'contacted' : 'invites', page: 1, open: '' });
                }}
                className={cn(
                  'inline-flex h-full items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors duration-[120ms]',
                  contactedOnly === tab.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.value ? <Users className="h-3.5 w-3.5" /> : <Inbox className="h-3.5 w-3.5" />}
                {tab.label}
                {tab.count !== null && <span className="tabular-nums opacity-80">{tab.count.toLocaleString()}</span>}
              </button>
            ))}
          </div>
          {excludingSections && (
            <p className="text-xs text-muted-foreground">
              {DEFAULT_EXCLUDED_LINKS.map((s) => s.label).join(' and ')} invites aren't in this view:{' '}
              {DEFAULT_EXCLUDED_LINKS.map((s, i) => (
                <Fragment key={s.team}>
                  {i > 0 && ' · '}
                  <Link
                    to={s.pick ? `${s.base}?team=${s.team}` : s.base}
                    className="font-medium text-foreground/80 underline-offset-4 hover:text-foreground hover:underline"
                  >
                    {s.label} → Invites
                  </Link>
                </Fragment>
              ))}
            </p>
          )}
        </div>

        {!contactedOnly && (
          <>
            <div className="border-b border-border">
              <InviteStageTabs value={stageTab} counts={stageCounts.data} onChange={selectStageTab} />
            </div>
            <InviteSavedViews active={savedView} counts={viewCounts.data} onSelect={selectSavedView} />
          </>
        )}

        {/* Primary filters — always visible. */}
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Search">
            <Input
              placeholder="@handle"
              value={f.search}
              onChange={(e) => set({ search: e.target.value, page: 1 })}
              className="w-full md:w-48"
            />
          </FilterField>
          {!contactedOnly && (
            <FilterField label="Logged">
              <div
                role="group"
                aria-label="Logged in the last"
                className="inline-flex h-9 items-center rounded-lg border border-input bg-[var(--input-bg)] p-0.5"
              >
                {[{ value: '', label: 'Any time' }, ...INVITE_SINCE_OPTIONS].map((o) => {
                  const active = f.since === o.value && (o.value !== '' || (!f.dateFrom && !f.dateTo));
                  return (
                    <button
                      key={o.value || 'any'}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        set(
                          o.value
                            ? { since: o.value, dateFrom: '', dateTo: '', page: 1 }
                            : { since: '', dateFrom: '', dateTo: '', page: 1 },
                        )
                      }
                      className={cn(
                        'h-full rounded-md px-2.5 text-xs font-medium transition-colors',
                        active ? 'bg-accent text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </FilterField>
          )}
          {!contactedOnly && (
            <FilterField label="Sort">
              <Combobox
                className="w-full md:w-48"
                aria-label="Sort invites"
                value={`${order.key}:${order.dir}`}
                onChange={(v) => {
                  const [key, dir] = (v || 'logged:desc').split(':');
                  sortBy(key as InviteSortKey, dir === 'asc' ? 'asc' : 'desc');
                }}
                options={INVITE_SORTS.flatMap((x) => [
                  { value: `${x.key}:desc`, label: x.highFirst },
                  { value: `${x.key}:asc`, label: x.lowFirst },
                ])}
              />
            </FilterField>
          )}
          {!locked && (
          <FilterField label="Team">
            <Combobox
              className="w-full md:w-44"
              value={team}
              onChange={(v) => setTeam(v)}
              options={(teams.data ?? [])
                .filter((t) => t.isActive)
                .map((t) => ({ value: t.code, label: t.displayName }))}
              allowAll
              allLabel={mainScopeLabel}
            />
          </FilterField>
          )}
          {contactedOnly ? (
            <FilterField label="Channel">
              <Combobox
                className="w-full md:w-40"
                value={f.channel}
                onChange={(v) => set({ channel: v as InviteChannel | '', page: 1 })}
                options={CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
                allowAll
                allLabel="All channels"
              />
            </FilterField>
          ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMore((v) => !v)}
            aria-expanded={showMore}
            aria-controls="invites-more-filters"
            className="text-muted-foreground hover:text-foreground"
          >
            {showMore ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showMore ? 'Fewer filters' : 'More filters'}
          </Button>
          )}
          {!contactedOnly && (
          <div className="flex items-center gap-2 md:ml-auto">
          <ColumnPicker columns={ADMIN_COLUMNS} visible={cols.visible} onToggle={cols.toggle} />
          {/* SPA-545. Download the currently-filtered rows as CSV. Same
            * params as the list query above so what admins see is what
            * they get. Backend caps at 25k rows and 413s past that. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void onExportClick()}
            disabled={exportMutation.isPending}
            title="Download the current filter as a CSV"
          >
            {exportMutation.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-1 h-3.5 w-3.5" />
            )}
            Export CSV
          </Button>
          </div>
          )}
        </div>

        {/* Secondary filters — channel / outreacher / niche / dates / owed flag. */}
        {!contactedOnly && showMore && (
          <div
            id="invites-more-filters"
            className="flex flex-wrap items-end gap-3 border-t pt-3"
          >
            <FilterField label="Channel">
              <Combobox
                className="w-full md:w-40"
                value={f.channel}
                onChange={(v) => set({ channel: v as InviteChannel | '', page: 1 })}
                options={CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
                allowAll
                allLabel="All channels"
              />
            </FilterField>
            <FilterField label="Outreacher">
              <OutreacherPicker
                className="w-full md:w-56"
                value={f.inviterId}
                onChange={(v) => set({ inviterId: v, page: 1 })}
                team={team || undefined}
                allowAll
                allLabel="All outreachers"
              />
            </FilterField>
            <FilterField label="Niche">
              <Combobox
                className="w-full md:w-56"
                value={f.niche}
                onChange={(v) => set({ niche: v, page: 1 })}
                options={(niches.data ?? [])
                  .slice()
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((n) => ({
                    value: n.code,
                    label: n.displayName,
                    // Hidden niches stay filterable: old invites still carry them.
                    ...(n.isHidden ? { hint: 'Hidden' } : n.isFocused ? { hint: '★' } : {}),
                  }))}
                allowAll
                allLabel="All niches"
                placeholder="Search niches…"
                emptyText="No niches match"
              />
            </FilterField>
            <FilterField label="Source">
              <Combobox
                className="w-full md:w-52"
                value={f.source}
                onChange={(v) => set({ source: v, page: 1 })}
                options={[
                  { value: '__any__', label: 'Any discovery queue' },
                  ...(discoveryFolders.data ?? []).map((name) => ({
                    value: name,
                    label: name,
                  })),
                ]}
                allowAll
                allLabel="All sources"
                placeholder="Filter by source…"
                emptyText="No discovery folders"
              />
            </FilterField>
            <FilterField label="From">
              <Input
                type="date"
                value={f.dateFrom}
                onChange={(e) => set({ dateFrom: e.target.value, since: '', page: 1 })}
                className="w-full md:w-40"
              />
            </FilterField>
            <FilterField label="To">
              <Input
                type="date"
                value={f.dateTo}
                onChange={(e) => set({ dateTo: e.target.value, since: '', page: 1 })}
                className="w-full md:w-40"
              />
            </FilterField>
            <FilterField label="Followers">
              <div className="flex items-center gap-1.5">
                <Input
                  inputMode="numeric"
                  placeholder="Min"
                  aria-label="Minimum followers"
                  value={f.minFollowers}
                  onChange={(e) => set({ minFollowers: e.target.value.replace(/[^\d]/g, ''), page: 1 })}
                  className="w-full md:w-28"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  inputMode="numeric"
                  placeholder="Max"
                  aria-label="Maximum followers"
                  value={f.maxFollowers}
                  onChange={(e) => set({ maxFollowers: e.target.value.replace(/[^\d]/g, ''), page: 1 })}
                  className="w-full md:w-28"
                />
              </div>
            </FilterField>
            <FilterField label="Avg views">
              <div className="flex items-center gap-1.5">
                <Input
                  inputMode="numeric"
                  placeholder="Min"
                  aria-label="Minimum average views"
                  value={f.minAvgViews}
                  onChange={(e) => set({ minAvgViews: e.target.value.replace(/[^\d]/g, ''), page: 1 })}
                  className="w-full md:w-28"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  inputMode="numeric"
                  placeholder="Max"
                  aria-label="Maximum average views"
                  value={f.maxAvgViews}
                  onChange={(e) => set({ maxAvgViews: e.target.value.replace(/[^\d]/g, ''), page: 1 })}
                  className="w-full md:w-28"
                />
              </div>
            </FilterField>
            <Button
              variant={f.owed ? 'default' : 'outline'}
              size="sm"
              aria-pressed={f.owed}
              onClick={() => set({ owed: !f.owed, page: 1 })}
            >
              Owed only
            </Button>
          </div>
        )}

        {/* Active filter chips — single-tap remove + clear all. */}
        <ActiveFilters filters={activeFilters} onClearAll={clearAllFilters} />
        {openMissing && (
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" role="status">
            The invite you followed a link to isn't on this page of results. Narrow the search or
            check the next page.
            {excludingSections && (
              <>
                Look under{' '}
                {sectionHrefs.map((s, i) => (
                  <Fragment key={s.team}>
                    {i > 0 && ' or '}
                    <Link to={s.href} className="text-foreground/80 underline-offset-2 hover:text-foreground hover:underline">
                      {s.label} → Invites
                    </Link>
                  </Fragment>
                ))}
              </>
            )}
            <button
              type="button"
              className="underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => set({ open: '' })}
            >
              Dismiss
            </button>
          </p>
        )}
      </div>

      {contactedOnly ? (
        <>
          <p className="text-sm text-muted-foreground">
            Creators someone messaged who haven't replied yet. A creator leaves this list as soon
            as any outreacher logs their reply. Nothing is owed for a message.
          </p>
          {contacted.isLoading ? (
            <TableSkeleton cols={['w-40', 'w-28', 'w-24', 'w-24', 'w-12', 'w-16']} />
          ) : (
            <ContactedTable rows={contactedRows} showWho />
          )}
          <Pagination
            page={contactedMeta.page}
            limit={contactedMeta.limit}
            total={contactedMeta.total}
            onPageChange={(p) => set({ page: p })}
          />
        </>
      ) : (
        <>
      {invites.isLoading ? (
        <TableSkeleton cols={['w-40', 'w-24', 'w-20', 'w-20', 'w-16', 'w-16']} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title={stageTab && stageTab !== 'all' ? `No ${INVITE_STAGE_TAB_LABELS[stageTab].toLowerCase()} invites match.` : 'No invites match.'}
          hint={
            activeFilters.length > 0 || savedView
              ? 'Clear an active filter above, or widen the date range.'
              : stageTab !== 'all'
                ? 'Try the All tab.'
                : 'Nothing has been logged for this view yet.'
          }
        />
      ) : (
        <DataTable>
              <DataTableHeader>
                <tr>
                  <DataTableHeaderCell>Creator</DataTableHeaderCell>
                  {cols.isVisible('outreachedBy') && <DataTableHeaderCell>Outreacher</DataTableHeaderCell>}
                  {cols.isVisible('stage') && <DataTableHeaderCell>Stage</DataTableHeaderCell>}
                  {cols.isVisible('date') && (
                    <DataTableHeaderCell aria-sort={ariaSort(order, 'logged')}>
                      <SortHeader label="Logged" sortKey="logged" order={order} onSort={toggleSort} />
                    </DataTableHeaderCell>
                  )}
                  {cols.isVisible('channel') && <DataTableHeaderCell>Channel</DataTableHeaderCell>}
                  {cols.isVisible('category') && <DataTableHeaderCell>Creator type</DataTableHeaderCell>}
                  {cols.isVisible('niche') && <DataTableHeaderCell>Niche</DataTableHeaderCell>}
                  {cols.isVisible('source') && <DataTableHeaderCell>Source</DataTableHeaderCell>}
                  {cols.isVisible('proof') && <DataTableHeaderCell>Proof</DataTableHeaderCell>}
                  {cols.isVisible('followers') && (
                    <DataTableHeaderCell numeric aria-sort={ariaSort(order, 'followers')}>
                      <SortHeader
                        label="Followers"
                        sortKey="followers"
                        order={order}
                        onSort={toggleSort}
                        title="Best-known follower count: from the creator's clipping account, else what discovery measured."
                      />
                    </DataTableHeaderCell>
                  )}
                  {cols.isVisible('avgViews') && (
                    <DataTableHeaderCell numeric aria-sort={ariaSort(order, 'avgViews')}>
                      <SortHeader
                        label="Avg views"
                        sortKey="avgViews"
                        order={order}
                        onSort={toggleSort}
                        title="Average views per post as discovery measured it. Blank for creators discovery never measured."
                      />
                    </DataTableHeaderCell>
                  )}
                  {cols.isVisible('posts') && (
                    <DataTableHeaderCell numeric aria-sort={ariaSort(order, 'posts')}>
                      <SortHeader label="Posts" sortKey="posts" order={order} onSort={toggleSort} />
                    </DataTableHeaderCell>
                  )}
                  {cols.isVisible('clipLinks') && <DataTableHeaderCell>Top clip</DataTableHeaderCell>}
                  {cols.isVisible('campaign') && <DataTableHeaderCell>Campaign</DataTableHeaderCell>}
                  {cols.isVisible('views') && (
                    <DataTableHeaderCell numeric aria-sort={ariaSort(order, 'views')}>
                      <SortHeader label="Clip views" sortKey="views" order={order} onSort={toggleSort} />
                    </DataTableHeaderCell>
                  )}
                  {cols.isVisible('owed') && (
                    <DataTableHeaderCell numeric aria-sort={ariaSort(order, 'owed')}>
                      <SortHeader label="Owed" sortKey="owed" order={order} onSort={toggleSort} title={GLOSSARY.owedBeforeAdjustments} />
                    </DataTableHeaderCell>
                  )}
                  <DataTableHeaderCell className="w-px">
                    <span className="sr-only">Actions</span>
                  </DataTableHeaderCell>
                </tr>
              </DataTableHeader>
              <tbody>
                {rows.map((inv) => {
                  const isHidden = inv.deletedAt !== null;
                  const url = profileUrl(inv.channel, inv.targetIdentifier);
                  const isSelected = selected?.id === inv.id;
                  // Creator type, niches and source, under the handle — they
                  // used to be three columns of their own.
                  const subline = [
                    creatorTypeLabel(inv.category),
                    inv.niches.map(nicheName).join(', '),
                    inv.discoveryFolderName ? `via ${inv.discoveryFolderName}` : '',
                  ].filter(Boolean).join(' · ');
                  return (
                    <Fragment key={inv.id}>
                    <DataTableRow
                      state={isSelected ? 'emphasis' : isHidden ? 'terminal' : 'default'}
                      className="cursor-pointer"
                      onClick={(e) => {
                        // Links, buttons and menus inside the row do their own
                        // thing. Portalled dialogs and menus still bubble React
                        // events up to here, hence the role checks.
                        if ((e.target as HTMLElement).closest('a,button,input,[role="menuitem"],[role="menu"],[role="dialog"],[role="alertdialog"]')) return;
                        openSheet(inv);
                      }}
                    >
                      <td className="px-3 py-2 font-medium">
                        <div className="flex min-w-0 flex-col">
                        <span className="inline-flex items-center gap-2">
                          <PlatformIcon
                            platform={inv.channel}
                            size={14}
                            className="text-muted-foreground"
                          />
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 hover:underline"
                              title={`Open ${channelLabel(inv.channel)} profile`}
                            >
                              {inv.targetIdentifier}
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            </a>
                          ) : (
                            <span>{inv.targetIdentifier}</span>
                          )}
                        </span>
                        {subline && (
                          <span className="ml-[22px] max-w-[18rem] truncate text-xs font-normal text-muted-foreground" title={subline}>
                            {subline}
                          </span>
                        )}
                        </div>
                      </td>
                      {cols.isVisible('outreachedBy') && (
                      <td className="px-3 py-2 text-muted-foreground" data-label="Outreacher">
                        {inviterNameById.get(inv.inviterId) ?? '—'}
                      </td>
                      )}
                      {cols.isVisible('stage') && (
                      <td className="px-3 py-2" data-label="Stage">
                        <InviteStagePill invite={inv} teamTracks={teamTracks(inv)} now={now} />
                      </td>
                      )}
                      {cols.isVisible('date') && (
                      <td className="whitespace-nowrap px-3 py-2 text-muted-foreground" data-label="Logged">{formatDate(inv.createdAt)}</td>
                      )}
                      {cols.isVisible('channel') && (
                      <td className="px-3 py-2" data-label="Channel">
                        <Tag tone={channelToneFor(inv.channel)}>
                          {channelLabel(inv.channel)}
                        </Tag>
                      </td>
                      )}
                      {cols.isVisible('category') && (
                      <td className="px-3 py-2" data-label="Creator type">{creatorTypeLabel(inv.category) || '—'}</td>
                      )}
                      {cols.isVisible('niche') && (
                      <td className="px-3 py-2 text-muted-foreground" data-label="Niche">
                        {inv.niches.length ? inv.niches.map(nicheName).join(', ') : '—'}
                      </td>
                      )}
                      {cols.isVisible('source') && (
                      <td className="px-3 py-2" data-label="Source">
                        {inv.discoveryFolderName ? (
                          <Tag tone="niche">{inv.discoveryFolderName}</Tag>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      )}
                      {cols.isVisible('proof') && (
                      <td className="px-3 py-2" data-label="Proof">
                        {inv.screenshotKey ? (
                          <Pill tone={verdictTone(inv.screenshotVerdict)} icon={false}>
                            {verdictLabel(inv.screenshotVerdict)}
                          </Pill>
                        ) : (
                          <span className="text-muted-foreground" title="No screenshot was uploaded">—</span>
                        )}
                      </td>
                      )}
                      {cols.isVisible('followers') && (
                      <td className="px-3 py-2 text-right text-muted-foreground" data-label="Followers">
                        {formatFollowerCount(inv.estFollowers ?? inv.followerCount)}
                      </td>
                      )}
                      {cols.isVisible('avgViews') && (
                      <td className="px-3 py-2 text-right text-muted-foreground" data-label="Avg views">
                        {formatFollowerCount(inv.avgViews)}
                      </td>
                      )}
                      {cols.isVisible('posts') && (
                      <td className="px-3 py-2 text-right text-muted-foreground" data-label="Posts">
                        {inv.postCount > 0
                          ? inv.postCount
                          : inv.hasPosted === false
                            ? '0'
                            : '—'}
                      </td>
                      )}
                      {cols.isVisible('clipLinks') && (() => {
                        const posts = inv.matchedUserId ? postsByUser[inv.matchedUserId] ?? [] : [];
                        const withUrl = posts.filter((p) => p.postUrl);
                        const top = withUrl.length > 0
                          ? withUrl.reduce((best, p) => (p.views ?? 0) > (best.views ?? 0) ? p : best)
                          : null;
                        return (
                          <td className="px-3 py-2" data-label="Top clip">
                            {top ? (
                              <a
                                href={top.postUrl!}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                <span className="text-xs">
                                  {top.views != null ? formatCompact(top.views) : '—'}
                                  {top.campaignName ? ` · ${top.campaignName}` : ''}
                                </span>
                              </a>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {withUrl.length > 1 && (
                              <span className="ml-1 text-[10px] text-muted-foreground">
                                +{withUrl.length - 1}
                              </span>
                            )}
                          </td>
                        );
                      })()}
                      {cols.isVisible('campaign') && (() => {
                        const posts = inv.matchedUserId ? postsByUser[inv.matchedUserId] ?? [] : [];
                        const campaigns = [...new Set(posts.map((p) => p.campaignName).filter(Boolean))];
                        return (
                          <td className="px-3 py-2 text-muted-foreground" data-label="Campaign">
                            {campaigns.length > 0 ? (
                              <span
                                className="block max-w-[14rem] truncate"
                                title={campaigns.join(', ')}
                              >
                                {campaigns[0]}
                                {campaigns.length > 1 ? ` +${campaigns.length - 1}` : ''}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        );
                      })()}
                      {cols.isVisible('views') && (() => {
                        const posts = inv.matchedUserId ? postsByUser[inv.matchedUserId] ?? [] : [];
                        const totalViews = posts.reduce((sum, p) => sum + (p.views ?? 0), 0);
                        return (
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground" data-label="Clip views">
                            {posts.length > 0 ? formatCompact(totalViews) : '—'}
                          </td>
                        );
                      })()}
                      {cols.isVisible('owed') && (
                      <td className="px-3 py-2 text-right" data-label="Owed">${formatMoney(owed(inv))}</td>
                      )}
                      {/* Compact on purpose: an icon to open the sheet and a
                          kebab, so the cell never pushes past the table edge. */}
                      <td className="actions w-px whitespace-nowrap px-2 py-1.5 text-right">
                        <div className="flex justify-end gap-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            aria-label={`Open details for ${inv.targetIdentifier}`}
                            title="Details, proof and payment history"
                            onClick={() => openSheet(inv)}
                          >
                            <PanelRight className="h-4 w-4" />
                          </Button>
                          <RowActions
                            triggerLabel={`More actions for ${inv.targetIdentifier}`}
                            items={rowMenu(inv)}
                          />
                        </div>
                      </td>
                    </DataTableRow>
                    </Fragment>
                  );
                })}
        </tbody>
      </DataTable>
      )}
      <Pagination
        page={meta.page}
        limit={meta.limit}
        total={meta.total}
        onPageChange={(p) => set({ page: p })}
      />
        </>
      )}

      {(() => {
        const idx = selected ? rows.findIndex((r) => r.id === selected.id) : -1;
        const inv = idx >= 0 ? rows[idx]! : selected;
        const go = (step: number) => {
          const next = rows[idx + step];
          if (next) openSheet(next);
        };
        return (
          <Sheet open={!!inv} onOpenChange={(open) => !open && closeSheet()}>
            {/* No autofocus: it lands on Next and draws a focus ring on open. */}
            <SheetContent onOpenAutoFocus={(e) => e.preventDefault()}>
              {inv && (
                <InviteSheetBody
                  invite={inv}
                  index={idx}
                  count={rows.length}
                  onPrev={() => go(-1)}
                  onNext={() => go(1)}
                  tracksForTeam={tracksForTeam}
                  knownInviterTeam={inviterTeamById.get(inv.inviterId) ?? null}
                  knownInviterName={inviterNameById.get(inv.inviterId) ?? null}
                  nicheName={nicheName}
                  me={me}
                  now={now}
                  actions={
                    <>
                      {sheetPrimary(inv)}
                      {sheetMore(inv).length > 0 && (
                        <RowActions
                          triggerText="More"
                          triggerLabel={`More actions for ${inv.targetIdentifier}`}
                          items={sheetMore(inv)}
                        />
                      )}
                    </>
                  }
                />
              )}
            </SheetContent>
          </Sheet>
        );
      })()}

      {auditing && <InviteScreenshotAuditDialog invite={auditing} onClose={() => setAuditing(null)} />}

      <Dialog open={!!unrejecting} onOpenChange={(open) => !open && setUnrejecting(null)}>
        <DialogContent className="sm:max-w-md">
          {unrejecting && (
            <>
              <DialogHeader>
                <DialogTitle>Un-reject "{unrejecting.targetIdentifier}"?</DialogTitle>
                <DialogDescription>
                  This changes the status only: the invite moves back to <strong>Joined</strong>.
                  The amount written off when it was rejected stays written off. To restore it,
                  use <strong>Adjust owed amount</strong> afterwards with a positive amount.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setUnrejecting(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={rowPending.has(`unreject:${unrejecting.id}`)}
                  onClick={() => void onUnreject(unrejecting)}
                >
                  {rowPending.has(`unreject:${unrejecting.id}`) && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Un-reject
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjusting} onOpenChange={(open) => !open && setAdjusting(null)}>
        <DialogContent>
          {adjusting && (
            <>
              <DialogHeader>
                <DialogTitle>Adjust owed amount for "{adjusting.targetIdentifier}"</DialogTitle>
                <DialogDescription>
                  Owed (before adjustments): ${formatMoney(owed(adjusting))}.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={adjustForm.handleSubmit(onAdjustSubmit)}
                className="grid gap-3"
                noValidate
              >
                <div className="grid gap-1">
                  <Label htmlFor="adjustAmount">Adjustment ($)</Label>
                  <Input
                    id="adjustAmount"
                    inputMode="decimal"
                    placeholder="-5.00 to reduce, 2.50 to increase"
                    {...adjustForm.register('amount', { required: 'Enter an amount' })}
                  />
                  {adjustForm.formState.errors.amount && (
                    <p className="text-xs text-destructive">{adjustForm.formState.errors.amount.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    A negative amount reduces what's owed; a positive one increases it. It's saved
                    to this invite's payment history with your reason and can't be edited later.
                  </p>
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="adjustReason">Reason</Label>
                  <Input
                    id="adjustReason"
                    placeholder="e.g. Outreacher reported the wrong amount, support refund"
                    {...adjustForm.register('reason', { required: 'Enter a reason', minLength: { value: 1, message: 'Enter a reason' } })}
                  />
                  {adjustForm.formState.errors.reason && (
                    <p className="text-xs text-destructive">{adjustForm.formState.errors.reason.message}</p>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAdjusting(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={adjustMutation.isPending}>
                    {adjustMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Save adjustment
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(open) => !open && setRejecting(null)}>
        <DialogContent>
          {rejecting && (
            <>
              <DialogHeader>
                <DialogTitle>Reject "{rejecting.targetIdentifier}"</DialogTitle>
                <DialogDescription>
                  Writes off the remaining ${formatMoney(owed(rejecting))}.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={rejectForm.handleSubmit(onRejectSubmit)}
                className="grid gap-3"
                noValidate
              >
                <div className="grid gap-1">
                  <Label htmlFor="rejectReason">Reason</Label>
                  <Input
                    id="rejectReason"
                    placeholder="e.g. Spam, duplicate, proof doesn't show a reply"
                    {...rejectForm.register('reason', { required: true, minLength: 1 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    The invite moves to Not paid and is left out of future payouts. Anything
                    already paid stays paid.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setRejecting(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={rejectMutation.isPending}
                  >
                    {rejectMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Reject invite
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!hiding} onOpenChange={(open) => !open && setHiding(null)}>
        <DialogContent>
          {hiding && (
            <>
              <DialogHeader>
                <DialogTitle>Hide "{hiding.targetIdentifier}"?</DialogTitle>
                <DialogDescription>
                  The invite disappears from the outreacher's lists. Admins still see it here,
                  marked Hidden. What's owed and what's been paid don't change. To write off
                  what's owed, use Reject instead.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setHiding(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => void onHideConfirm()}
                >
                  {deleteMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Hide invite
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassigning} onOpenChange={(open) => !open && setReassigning(null)}>
        <DialogContent>
          {reassigning && (
            <>
              <DialogHeader>
                <DialogTitle>Reassign "{reassigning.targetIdentifier}"</DialogTitle>
                <DialogDescription>
                  Currently with{' '}
                  {inviterNameById.get(reassigning.inviterId) ?? 'another outreacher'}.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={reassignForm.handleSubmit(onReassignSubmit)}
                className="grid gap-3"
                noValidate
              >
                <div className="grid gap-1">
                  <Label htmlFor="reassignInviter">New outreacher</Label>
                  <OutreacherPicker
                    id="reassignInviter"
                    value={reassignForm.watch('inviterId')}
                    onChange={(v, name) => {
                      reassignForm.setValue('inviterId', v, { shouldDirty: true });
                      setReassignName(name ?? '');
                    }}
                    activeOnly
                    team={locked ? team : undefined}
                    currentId={reassigning.inviterId}
                    placeholder="Search outreachers…"
                  />
                  <p className="text-xs text-muted-foreground">
                    Only the owner changes. What the invite earned and any adjustments move with
                    it. Amounts already paid stay credited to the previous outreacher; what's still
                    owed moves to the new one.
                  </p>
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="reassignReason">Reason (optional)</Label>
                  <Input
                    id="reassignReason"
                    placeholder="e.g. Logged under the wrong outreacher"
                    {...reassignForm.register('reason')}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setReassigning(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={reassignMutation.isPending}>
                    {reassignMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Reassign
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>Edit handle & niches for "{editing.targetIdentifier}"</DialogTitle>
                <DialogDescription>
                  Fix the creator's handle or niche tags. Status, owed amount and outreacher
                  don't change here; use their own actions for those.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={editForm.handleSubmit(onEditSubmit)}
                className="grid gap-3"
                noValidate
              >
                <div className="grid gap-1">
                  <Label htmlFor="editTarget">Handle</Label>
                  <Input
                    id="editTarget"
                    placeholder="handle (no @)"
                    autoComplete="off"
                    {...editForm.register('targetIdentifier', { required: true })}
                  />
                  <p className="text-xs text-muted-foreground">
                    A leading <code>@</code> is removed. The platform ({channelLabel(editing.channel)})
                    can't change; log a new invite if it's wrong.
                  </p>
                </div>
                <div className="grid gap-1">
                  <Label>Niches</Label>
                  <NichesPicker
                    niches={(niches.data ?? []).filter(
                      (n) =>
                        !n.isHidden &&
                        (n.categoryCodes.length === 0 ||
                          n.categoryCodes.includes(editing.category)),
                    )}
                    nameOf={nicheName}
                    value={editForm.watch('niches')}
                    onChange={(next) =>
                      editForm.setValue('niches', next, { shouldDirty: true })
                    }
                    loading={niches.isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Click to toggle. Only niches that fit this creator type
                    ({creatorTypeLabel(editing.category)}) are shown.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Save changes
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The detail sheet's header (anchor, stage, context line, Prev / Next and
 * every action) and body. A component of its own so it can look up the
 * outreacher when the capped inviters list doesn't include them.
 */
function InviteSheetBody({
  invite,
  index,
  count,
  onPrev,
  onNext,
  tracksForTeam,
  knownInviterTeam,
  knownInviterName,
  nicheName,
  me,
  now,
  actions,
}: {
  invite: OutreachInvite;
  index: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
  tracksForTeam: (team: string | null | undefined) => boolean | undefined;
  knownInviterTeam: string | null;
  knownInviterName: string | null;
  nicheName: (code: string) => string;
  me: { id: string | null; name: string | null };
  now: Date;
  actions: React.ReactNode;
}): JSX.Element {
  const inviter = useAdminInviter(invite.inviterId);
  const inviterName = inviter.data?.name ?? knownInviterName;
  const tracks = tracksForTeam(inviter.data?.team ?? knownInviterTeam);
  const url = profileUrl(invite.channel, invite.targetIdentifier);
  return (
    <>
      <div className="space-y-3 border-b border-border px-5 pb-4 pt-5">
        <div className="flex items-start justify-between gap-3 pr-8">
          <div className="min-w-0">
            <SheetTitle className="flex items-center gap-2 text-lg font-semibold">
              <PlatformIcon platform={invite.channel} size={16} className="text-muted-foreground" />
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-w-0 items-center gap-1 truncate hover:underline"
                  title={`Open ${channelLabel(invite.channel)} profile`}
                >
                  {invite.targetIdentifier}
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </a>
              ) : (
                <span className="truncate">{invite.targetIdentifier}</span>
              )}
              <InviteStagePill invite={invite} teamTracks={tracks} now={now} />
            </SheetTitle>
            <SheetDescription className="mt-1 text-sm text-muted-foreground">
              {[
                `Outreacher ${inviterName ?? '—'}`,
                `Logged ${formatDate(invite.createdAt)}`,
                creatorTypeLabel(invite.category),
                invite.niches.map(nicheName).join(', '),
              ].filter(Boolean).join(' · ')}
            </SheetDescription>
          </div>
          {index >= 0 && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                disabled={index <= 0}
                onClick={onPrev}
                aria-label="Previous invite"
                title="Previous invite"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs tabular-nums text-muted-foreground">
                {index + 1}/{count}
              </span>
              <Button
                size="icon"
                variant="ghost"
                disabled={index >= count - 1}
                onClick={onNext}
                aria-label="Next invite"
                title="Next invite"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <InviteDetailPanel
          invite={invite}
          inviterName={inviterName}
          inviterPublicId={inviter.data?.publicId ?? null}
          nicheName={nicheName}
          me={me}
          stageKey={inviteStage(invite, { teamTracks: tracks }).key}
          now={now}
        />
      </div>
    </>
  );
}

function owed(inv: OutreachInvite): number {
  const locked = inv.lockedValue ? parseFloat(inv.lockedValue) : 0;
  return Math.max(0, locked - parseFloat(inv.amountPaid));
}

/**
 * Public profile URL for the prospect, when we can build one. Discord has no
 * public profile URL given just a username (we'd need a guild id +
 * member-resolved discord id to deep-link), so it returns null.
 */
function profileUrl(channel: string, targetIdentifier: string): string | null {
  const handle = targetIdentifier.trim().replace(/^@+/, '');
  if (!handle) return null;
  const encoded = encodeURIComponent(handle);
  switch (channel) {
    case 'TIKTOK':
      return `https://www.tiktok.com/@${encoded}`;
    case 'YOUTUBE':
      return `https://www.youtube.com/@${encoded}`;
    case 'INSTAGRAM':
      return `https://www.instagram.com/${encoded}/`;
    case 'TWITTER':
      return `https://x.com/${encoded}`;
    case 'DISCORD':
      return null;
    default:
      return null;
  }
}

type ListOrder = { key: InviteSortKey; dir: 'asc' | 'desc' };

function ariaSort(order: ListOrder, key: InviteSortKey): 'ascending' | 'descending' | 'none' {
  return order.key !== key ? 'none' : order.dir === 'asc' ? 'ascending' : 'descending';
}

/** A column header that orders the list by that column: high to low, then low to high. */
function SortHeader({
  label,
  sortKey,
  order,
  onSort,
  title,
}: {
  label: string;
  sortKey: InviteSortKey;
  order: ListOrder;
  onSort: (key: InviteSortKey) => void;
  title?: string;
}): JSX.Element {
  const active = order.key === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn('inline-flex items-center gap-1 hover:text-foreground', active && 'text-foreground')}
      title={title ?? (active ? `Sorted: ${sortLabel(sortKey, order.dir)}. Click to reverse.` : `Sort by ${label.toLowerCase()}`)}
    >
      {label}
      {!active ? (
        <ArrowUpDown className="h-3 w-3 opacity-60" />
      ) : order.dir === 'asc' ? (
        <ArrowUp className="h-3 w-3" />
      ) : (
        <ArrowDown className="h-3 w-3" />
      )}
    </button>
  );
}

function formatFollowerCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/**
 * An event-date filter as a chip label. The days are what the link sent:
 * `to` is the exclusive end (a UTC midnight), so the last day shown is the
 * day before it.
 */
function eventRangeLabel(from: string, to: string): string {
  const last = to ? new Date(Date.parse(`${to}T00:00:00Z`) - 24 * 60 * 60 * 1000) : null;
  const lastDay = last && !Number.isNaN(last.getTime()) ? formatDate(`${last.toISOString().slice(0, 10)}T12:00:00Z`) : '';
  const firstDay = from ? formatDate(`${from}T12:00:00Z`) : '';
  if (firstDay && lastDay) return `${firstDay} – ${lastDay}`;
  if (firstDay) return `from ${firstDay}`;
  return lastDay ? `up to ${lastDay}` : '';
}

/** Tab label for a stage filter value ("not_paid" → "Not paid"). */
const INVITE_STAGE_TAB_LABELS: Record<InviteStageFilter, string> = {
  replied: INVITE_STAGES.replied.label,
  joined: INVITE_STAGES.joined.label,
  posted: INVITE_STAGES.posted.label,
  expired: INVITE_STAGES.expired.label,
  not_paid: INVITE_STAGES.notPaid.label,
};

/**
 * A mixed stage selection (from a link) as a chip label: one or two stages
 * by name, three or more as "A +N more".
 */
function describeStages(stages: readonly InviteStageFilter[]): string {
  const labels = stages.map((s) => INVITE_STAGE_TAB_LABELS[s]);
  if (labels.length <= 2) return labels.join(', ');
  return `${labels[0]} +${labels.length - 1} more`;
}

/**
 * The detail sheet's body. Shows what the table can't carry: the money
 * breakdown, the activity timeline (logged → last checked → posted), and
 * the context fields (outreacher, niches, proof, contact, notes, posts).
 */
function InviteDetailPanel({
  invite,
  inviterName,
  inviterPublicId,
  nicheName,
  me,
  stageKey,
  now,
}: {
  invite: OutreachInvite;
  inviterName: string | null;
  inviterPublicId: string | null;
  nicheName: (code: string) => string;
  me: { id: string | null; name: string | null };
  stageKey: string;
  now: Date;
}): JSX.Element {
  const owedAmount = owed(invite);
  const postsQuery = useInvitePosts(invite.matchedUserId ? invite.id : null);
  const posts: InvitePost[] = postsQuery.data?.posts ?? [];
  const row = invite as InviteRow;
  const reviewedBy = row.screenshotReviewedBy ?? null;
  const stageAge = stageAgeLabel(stageKey, row, now);
  // Clip views are summed from clipping's posts, so they only exist for a
  // creator clipping has matched.
  const clipViews = !invite.matchedUserId
    ? '—'
    : postsQuery.isLoading
      ? '…'
      : postsQuery.data?.unavailable
        ? '—'
        : formatCompact(posts.reduce((sum, p) => sum + (p.views ?? 0), 0));
  const proof = invite.screenshotKey
    ? [
        verdictLabel(invite.screenshotVerdict),
        invite.screenshotVerdict
          ? `reviewed by ${actorLabel(reviewedBy, me).replace(/^(You|An admin|System)$/, (m) => m.toLowerCase())}${
              invite.screenshotReviewedAt ? ` on ${formatDate(invite.screenshotReviewedAt)}` : ''
            }`
          : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : 'No screenshot';

  return (
    <div className="space-y-5 px-5 py-4 text-sm">
      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4">
        <Fact
          label="In stage"
          value={stageAge ?? '—'}
          hint={stageAge ? undefined : "This invite doesn't record when it reached its stage."}
        />
        <Fact label="Owed" value={`$${formatMoney(owedAmount)}`} hint={GLOSSARY.owedBeforeAdjustments} />
        <Fact label="Followers" value={formatFollowerCount(invite.estFollowers ?? invite.followerCount)} />
        <Fact
          label="Clip views"
          value={clipViews}
          hint={invite.matchedUserId ? 'Views across every approved clip clipping returned.' : 'Clipping hasn\'t matched this creator yet.'}
        />
      </dl>

      {/* Proof up front: reviewing it shouldn't need the More menu. Keyed so
          the note box resets when Prev / Next moves to another invite. */}
      <InviteProofInline key={invite.publicId} invite={invite} />

      <div className="grid gap-5 sm:grid-cols-2">
        <DetailSection title="Money">
          <DetailRow label="Earned" value={`$${formatMoney(invite.lockedValue)}`} />
          <DetailRow label="Paid" value={`$${formatMoney(invite.amountPaid)}`} />
          <DetailRow
            label="Owed (before adjustments)"
            value={`$${formatMoney(owedAmount)}`}
            emphasis={owedAmount > 0}
          />
          <DetailRow
            label="Follower bonus"
            value={`$${formatMoney(invite.bracketBonusAttributed)}`}
          />
          {invite.outreachPayoutId !== null && (
            <DetailRow
              label="Payout"
              value={
                <Link
                  to={`/admin/payouts?inviter=${invite.inviterId}`}
                  className="text-primary hover:underline"
                >
                  View payouts
                </Link>
              }
            />
          )}
        </DetailSection>

        <DetailSection title="Activity">
          <DetailRow label="Logged" value={formatDateTime(invite.createdAt)} />
          {row.verifiedAt && <DetailRow label="Joined" value={formatDateTime(row.verifiedAt)} />}
          {invite.convertedAt && <DetailRow label="Posted" value={formatDateTime(invite.convertedAt)} />}
          {invite.releasedAt && <DetailRow label="Expired" value={formatDateTime(invite.releasedAt)} />}
          {invite.deletedAt && <DetailRow label="Hidden" value={formatDateTime(invite.deletedAt)} />}
          <DetailRow label="Last checked" value={formatDateTime(invite.lastResolvedAt)} />
          <DetailRow
            label="Posts"
            value={
              invite.postCount > 0
                ? String(invite.postCount)
                : invite.hasPosted === false
                  ? 'None yet'
                  : '—'
            }
          />
          {invite.isInServer !== null && invite.channel === 'DISCORD' && (
            <DetailRow
              label="In server"
              value={invite.isInServer ? 'Yes' : 'No'}
            />
          )}
        </DetailSection>

        <div className="sm:col-span-2">
        <DetailSection title="Context">
          <DetailRow
            label="Outreacher"
            value={
              inviterPublicId ? (
                <Link to={`/admin/outreachers/${inviterPublicId}`} className="text-primary hover:underline">
                  {inviterName ?? 'Open profile'}
                </Link>
              ) : (
                inviterName ?? '—'
              )
            }
          />
          <DetailRow label="Creator type" value={creatorTypeLabel(invite.category) || '—'} />
          <DetailRow
            label="Niches"
            value={invite.niches.length > 0 ? invite.niches.map(nicheName).join(', ') : '—'}
          />
          <DetailRow label="Source" value={invite.discoveryFolderName ?? 'Logged by hand'} />
          <DetailRow label="Proof" value={proof} />
          {invite.screenshotReviewNote && (
            <DetailRow label="Review note" value={`"${invite.screenshotReviewNote}"`} />
          )}
          <DetailRow label="Email" value={invite.email ?? '—'} />
          <DetailRow
            label="Invite ID"
            value={invite.publicId}
            mono
          />
          {invite.matchedUserId && (
            <DetailRow label="Clipping account" value={invite.matchedUserId} mono />
          )}
          {invite.notes && (
            <div className="mt-1 border-t border-border pt-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Notes
              </div>
              <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-foreground/85">
                {invite.notes}
              </p>
            </div>
          )}
        </DetailSection>
        </div>
      </div>

      {invite.matchedUserId && (
        <div id={CLIPS_SECTION_ID} className="scroll-mt-4 border-t border-border pt-3">
          <AttributionBanner
            bufferHours={postsQuery.data?.bufferHours}
            windowStart={postsQuery.data?.windowStart}
            posts={posts}
            postsUnavailable={postsQuery.data?.unavailable}
            postCount={invite.postCount}
            hasPosted={invite.hasPosted}
          />
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">
            Approved clips
          </div>
          {postsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading posts…
            </div>
          ) : postsQuery.data?.unavailable ? (
            <p className="text-xs text-muted-foreground">Couldn't reach clipping. Try again in a minute.</p>
          ) : posts.length === 0 ? (
            // "None" here is truthful: `/invite-posts` returned an empty array
            // for this identifier. Distinct from "we couldn't reach clipping"
            // (unavailable, above). If a creator seems to have posted but
            // nothing shows up, the invite's identifier didn't classify —
            // check invitePostsIdentifier on the backend.
            <p className="text-xs text-muted-foreground">
              Clipping has no approved clips for this creator.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Post</th>
                    <th className="px-2 py-1.5 text-left">Campaign</th>
                    <th className="px-2 py-1.5 text-right">Views</th>
                    <th className="px-2 py-1.5 text-right">Likes</th>
                    <th className="px-2 py-1.5 text-right">Comments</th>
                    <th className="px-2 py-1.5 text-right">Engagement</th>
                    <th className="px-2 py-1.5 text-left">Posted</th>
                    <th className="px-2 py-1.5 text-left">Counts</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.postId} className="border-t border-border/50">
                      <td className="px-2 py-1.5">
                        {post.postUrl ? (
                          <a
                            href={post.postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            {post.platform}
                            <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{post.platform}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">
                        {post.campaignName ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {post.views != null ? formatCompact(post.views) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {post.likes != null ? formatCompact(post.likes) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {post.comments != null ? formatCompact(post.comments) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {post.engagementRate != null
                          ? `${(post.engagementRate * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">
                        {formatDate(post.postedAt)}
                      </td>
                      <td className="px-2 py-1.5">
                        <PostAttributionBadge
                          inWindow={post.inWindow}
                          filteredReason={post.filteredReason}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <PaymentHistoryTab invitePublicId={invite.publicId} />
    </div>
  );
}

/**
 * One-glance attribution banner. Shows the actual window that applied to this
 * invite (bufferHours from the inviter's team) + in-window vs all-time counts,
 * so the classic "why isn't this converting?" question is self-answered before
 * the admin has to squint at Clip.createdAt timestamps.
 *
 * When posts couldn't be fetched (clipping unreachable) or the creator's clip
 * activity has been window-filtered, the banner renders a one-line hint that
 * points at the actual lever (team `attributionBufferHours`).
 */
function AttributionBanner({
  bufferHours,
  windowStart,
  posts,
  postsUnavailable,
  postCount,
  hasPosted,
}: {
  bufferHours?: number;
  windowStart?: string;
  posts: InvitePost[];
  postsUnavailable: boolean | undefined;
  postCount: number;
  hasPosted: boolean | null;
}): JSX.Element | null {
  if (bufferHours === undefined || !windowStart) return null;
  const windowStartAt = new Date(windowStart);
  if (Number.isNaN(windowStartAt.getTime())) return null;
  const inWindowCount = posts.filter((p) => p.inWindow === true).length;
  const outOfWindowCount = posts.filter((p) => p.inWindow === false).length;
  // "All-time" here = every post clipping returned for this invite. It's a
  // proxy for the /resolve-targets all-time count and matches what the writer
  // saw. When posts are unavailable we fall back to postCount which is the
  // last-known-good value from a prior successful resolve.
  const allTime = postsUnavailable ? postCount : posts.length;
  const hint = pickAttributionHint({
    postsUnavailable,
    hasWindowedPost: inWindowCount > 0 || (postsUnavailable ? hasPosted === true : false),
    hasOutOfWindowPost: outOfWindowCount > 0,
    allTime,
  });
  return (
    <div className="mb-3 rounded-md border border-border/70 bg-muted/20 px-3 py-2 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div>
          <span className="text-muted-foreground">Counts posts from: </span>
          <span className="text-[11px] text-foreground/90">
            {formatDateTime(windowStartAt)} onward
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Starts before logging: </span>
          <span className="text-[11px] tabular-nums text-foreground/90">
            {bufferHours}h ({(bufferHours / 24).toFixed(bufferHours % 24 === 0 ? 0 : 1)} days)
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Counted: </span>
          <span className="text-[11px] text-foreground/90 tabular-nums">
            {inWindowCount} of {allTime}
          </span>
        </div>
      </div>
      {hint && (
        <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/**
 * Pure hint picker — the frontend equivalent of the backend's AttributionReason,
 * but derived from the /posts response (no ResolveTargetResult in scope). Kept
 * inline because it's tightly coupled to the banner's copy tone.
 */
function pickAttributionHint(input: {
  postsUnavailable: boolean | undefined;
  hasWindowedPost: boolean;
  hasOutOfWindowPost: boolean;
  allTime: number;
}): string | null {
  if (input.postsUnavailable) return "Clipping didn't respond. Try Re-check status.";
  if (input.hasWindowedPost) return null;
  if (input.hasOutOfWindowPost) {
    return "Approved clips exist, but every one is from before this invite's credit window. Widen the team's credit window in Setup, or re-log the invite to move it.";
  }
  if (input.allTime === 0) return 'No approved clips found for this creator yet.';
  return null;
}

/**
 * Per-clip in-window badge. Muted "Out" chip carries a tooltip that pinpoints
 * WHY the clip was filtered — `before_window` is the common case, but
 * `no_postedAt` and `unparseable_postedAt` can happen too.
 */
function PostAttributionBadge({
  inWindow,
  filteredReason,
}: {
  inWindow?: boolean;
  filteredReason?: 'no_postedAt' | 'unparseable_postedAt' | 'before_window' | null;
}): JSX.Element {
  if (inWindow === undefined) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  if (inWindow) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
        Yes
      </span>
    );
  }
  const reasonLabel =
    filteredReason === 'no_postedAt'
      ? 'No post date'
      : filteredReason === 'unparseable_postedAt'
        ? "Post date couldn't be read"
        : filteredReason === 'before_window'
          ? 'Posted before the credit window'
          : 'Outside the credit window';
  return (
    <span
      className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
      title={reasonLabel}
    >
      No
    </span>
  );
}

/** One figure in the sheet's facts strip. */
function Fact({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }): JSX.Element {
  return (
    <div className="bg-card px-3 py-2.5" title={hint}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-mono text-base font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </div>
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
  emphasis = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  emphasis?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'truncate text-right text-foreground/90',
          mono && 'font-mono text-[11px]',
          emphasis && 'font-semibold text-foreground',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
